import React, { useState } from 'react';
import { X, User, Mail, FileText, Download } from 'lucide-react';

interface ResourceDownloadFormProps {
  isOpen: boolean;
  onClose: () => void;
  resourceTitle: string;
  resourceType: string;
  resourceDescription: string;
}

const ResourceDownloadForm: React.FC<ResourceDownloadFormProps> = ({ 
  isOpen, 
  onClose, 
  resourceTitle,
  resourceType,
  resourceDescription
}) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    resourceType: resourceType
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formBody = {
        'form-name': 'resource-download',
        ...formData
      };

      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formBody).toString()
      });

      if (response.ok) {
        // Simulate download by opening a new tab
        // In a real implementation, this would redirect to the actual resource
        window.open('/success', '_blank');
        onClose();
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('There was an error processing your download. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-lg font-semibold">Download {resourceTitle}</h2>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4">
          <div className="mb-4">
            <div className="flex items-center justify-center w-12 h-12 mx-auto bg-ta-green/10 rounded-full">
              <FileText className="h-6 w-6 text-ta-green" />
            </div>
            <p className="mt-2 text-center text-gray-600">{resourceDescription}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Your Name *</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleInputChange}
                  className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-ta-green focus:ring-ta-green"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-ta-green focus:ring-ta-green"
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">We'll send you updates about Uganda travel</p>
            </div>

            <input type="hidden" name="resourceType" value={resourceType} />

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-3 bg-ta-green text-white rounded-lg hover:bg-ta-green-dark transition-colors flex items-center justify-center"
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </>
              ) : (
                <>
                  <Download className="mr-2 h-5 w-5" />
                  Download Now
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResourceDownloadForm;